import { useState } from "react";
import { Text } from "../../../../components/common/typography";
import { Badge } from "../../../../components/common/badge";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { Button } from "../../../../components/common/button";
import { UpdateStockDialog } from "./update-stock-dialog";
import { productVariantsService } from "../../../api_client";
import { showSuccessToast, showErrorToast } from "../../../../utils/toasts";
import { mutate } from "swr";
import type { ProductVariantDto } from "../../../../api";
import { formatDisplayName } from "../../utils/format-display-name";

interface ProductVariantSelectorProps {
  variants: ProductVariantDto[];
  selectedVariantId: number | null;
  onVariantChange: (variantId: number | null) => void;
  enableStockUpdate?: boolean;
  productId?: number;
}

export function ProductVariantSelector({
  variants,
  selectedVariantId,
  onVariantChange,
  enableStockUpdate = false,
  productId,
}: ProductVariantSelectorProps) {
  const [selectedVariant, setSelectedVariant] =
    useState<ProductVariantDto | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleUpdateStock = async (variantId: number, newStock: number) => {
    try {
      // The API expects productId (which is the variant ID in this context) as first parameter
      await productVariantsService.updateProductVariantStock(variantId, {
        supplierStock: newStock,
      });

      showSuccessToast("Stock updated successfully");

      // Mutate the product variants cache to refresh data
      if (productId !== undefined) {
        await mutate(`product-${productId}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update stock. Please try again.";
      showErrorToast(errorMessage);
      throw error;
    }
  };

  const openUpdateDialog = (
    variant: ProductVariantDto,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setSelectedVariant(variant);
    setIsDialogOpen(true);
  };

  if (!variants || variants.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <Text variant="bodyLgBold" tone="dark" className="mb-1">
          Select Variant
        </Text>
        <div className="flex flex-col gap-2">
          {variants.map((variant) => {
            const isSelected = selectedVariantId === variant.id;
            const priceExtra = variant.priceExtra ?? 0;
            const variantQty = variant.supplierStock ?? 0;
            const isOutOfStock = variantQty === 0;
            const isLowStock = variantQty > 0 && variantQty < 20;
            const formattedName = formatDisplayName(
              variant.displayName,
              undefined,
              true
            );
            const displayNameParts = formattedName
              ? formattedName.split("\n")
              : [];

            return (
              <div
                key={variant.id}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-uiSecondary"
                }`}
              >
                <button
                  onClick={() => onVariantChange(variant.id)}
                  disabled={isOutOfStock}
                  className={`flex-1 flex items-center justify-between ${
                    isOutOfStock
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <div className="flex-1 flex flex-col items-start gap-1">
                    <div className="flex flex-col items-start">
                      <Text variant="bodyLg" tone="dark" className="text-left">
                        {displayNameParts.length > 0
                          ? displayNameParts[0]
                          : variant.name || "Variant"}
                      </Text>
                      {displayNameParts.length > 1 && (
                        <Text variant="body" tone="body" className="text-left whitespace-pre-line">
                          {displayNameParts.slice(1).join("\n")}
                        </Text>
                      )}
                    </div>
                    {variant.defaultCode && (
                      <Text variant="bodyXs" tone="body">
                        SKU: {variant.defaultCode}
                      </Text>
                    )}
                    {priceExtra !== 0 && (
                      <div className="flex items-center gap-2 mt-1">
                        <Text variant="bodySm" as="span">
                          +{" "}
                          <CurrencyAmount
                            amount={priceExtra}
                            currency="QAR"
                            variant="bodySm"
                          />
                        </Text>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 ml-4">
                    {isOutOfStock ? (
                      <Badge label="Out of Stock" state="error" />
                    ) : isLowStock ? (
                      <Badge label="Low Stock" state="pending" />
                    ) : (
                      <Text variant="bodyXs">
                        In Stock
                      </Text>
                    )}
                    <Text variant="bodyXs" tone="body">
                      Qty: {variantQty}
                    </Text>
                  </div>
                </button>
                {enableStockUpdate && (
                  <Button variant="primary"
                    onClick={(e) => openUpdateDialog(variant, e)}
                    className="ml-2 flex-shrink-0"
                  >
                    Update Stock
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {enableStockUpdate && (
        <UpdateStockDialog
          open={isDialogOpen}
          setOpen={setIsDialogOpen}
          variant={selectedVariant}
          currentStock={selectedVariant?.supplierStock ?? 0}
          onUpdate={handleUpdateStock}
        />
      )}
    </>
  );
}
